/**
 * Filter bar for stats tab – compact horizontal flow, max width utilisation.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { Bookmark, Filter, Plus } from 'lucide-react';

import { Button, Switch } from '@/components/ui';
import { FILTER_FIELDS } from '../shared/constants';
import type { FiltersState, FilterConfig } from '../shared';
import type { SavedFilter } from '@/features/smswiser-campaigns/types/saved-filter.types';
import { FilterRow } from './FilterRow';
import { PresetDropdown } from './presets';

interface FilterFieldOption {
  key: string;
  label: string;
  type: 'text' | 'numeric';
}

export interface FilterBarProps {
  filtersState: FiltersState;
  setFilters: (updater: (p: FiltersState) => FiltersState) => void;
  rowsLength: number;
  allRowsLength: number;
  filterFields?: FilterFieldOption[];
  tagFilterOptions?: string[];
  tagFilterColors?: Record<string, string>;
  tagsLoading?: boolean;
  staticTagFilterOptions?: string[];
  staticTagFilterColors?: Record<string, string>;
  staticTagsLoading?: boolean;
  defaultFilter: FilterConfig;
  presets?: SavedFilter[];
  activePresetId?: string | null;
  onLoadPreset?: (preset: SavedFilter) => void;
  onSaveClick?: () => void;
  onEditPreset?: (preset: SavedFilter) => void;
  onDeletePreset?: (id: string) => void;
  onSharePreset?: (preset: SavedFilter) => void;
}

export const FilterBar: FC<FilterBarProps> = ({
  filtersState, setFilters, rowsLength, allRowsLength,
  filterFields: filterFieldsProp, tagFilterOptions, tagFilterColors,
  tagsLoading, staticTagFilterOptions, staticTagFilterColors,
  staticTagsLoading, defaultFilter,
  presets, activePresetId, onLoadPreset, onSaveClick,
  onEditPreset, onDeletePreset, onSharePreset,
}) => {
  const fields = filterFieldsProp ?? FILTER_FIELDS;
  const hasActiveFilters = filtersState.active && filtersState.filters.some((f) => f.value.trim());

  return (
    <div className="relative flex w-full flex-wrap items-center gap-x-1.5 gap-y-1 rounded-lg border border-border/30 px-2.5 py-1.5 pr-[8.5rem]">
      <div className="absolute right-2 top-1.5 flex items-center gap-2">
        {hasActiveFilters && (
          <span className="text-[11px] tabular-nums text-muted-foreground/50">
            {rowsLength} of {allRowsLength} rows
          </span>
        )}
        {onSaveClick && (
          <button onClick={onSaveClick} data-testid="filter-bar-save" aria-label="Save filter preset"
            className="flex h-5 w-5 items-center justify-center rounded-sm text-muted-foreground/50 transition-colors hover:text-primary">
            <Bookmark className="h-3.5 w-3.5" />
          </button>
        )}
        <Switch id="filter-toggle" checked={filtersState.active}
          onCheckedChange={(v) => setFilters((p) => ({ ...p, active: v }))} />
        <label htmlFor="filter-toggle" className="sr-only">
          Filters {filtersState.active ? 'on' : 'off'}
        </label>
      </div>

      <span className="inline-flex w-[23px] shrink-0 items-center justify-center">
        <Filter className={`h-3.5 w-3.5 transition-colors ${filtersState.active ? 'text-primary' : 'text-muted-foreground/40'}`} />
      </span>

      {presets && presets.length > 0 && onLoadPreset && (
        <PresetDropdown presets={presets} activePresetId={activePresetId ?? null}
          onLoad={onLoadPreset} onEdit={(p) => onEditPreset?.(p)}
          onDelete={(id) => onDeletePreset?.(id)} onShare={(p) => onSharePreset?.(p)} />
      )}

      {filtersState.filters.map((filter, i) => (
        <div key={i} className="flex items-center gap-1">
          {i > 0 && (
            <span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground/60 select-none">AND</span>
          )}
          <FilterRow filter={filter}
            onUpdate={(updater) => setFilters((p) => ({ ...p, filters: p.filters.map((f, j) => (j === i ? updater(f) : f)) }))}
            onRemove={() => setFilters((p) => ({ ...p, filters: p.filters.filter((_, j) => j !== i) }))}
            fields={fields} tagFilterOptions={tagFilterOptions} tagFilterColors={tagFilterColors}
            staticTagFilterOptions={staticTagFilterOptions} staticTagFilterColors={staticTagFilterColors}
            tagsLoading={tagsLoading} staticTagsLoading={staticTagsLoading}
            canRemove={filtersState.filters.length > 1} />
        </div>
      ))}

      <Button variant="ghost" size="sm" data-testid="filter-bar-add"
        className="h-7 gap-1 px-2 text-[11px] text-muted-foreground hover:text-foreground"
        onClick={() => setFilters((p) => ({ ...p, filters: [...p.filters, { ...defaultFilter, value: '' }] }))}>
        <Plus className="h-3 w-3" /> Add
      </Button>

    </div>
  );
};
